<template lang="pug">
  validation-observer(ref="obs" v-slot="{ invalid }")
    validation-provider(
      v-for="field in fields"
      :key="field.value"
      :rules="field.rules"
      :name="field.text"
      :vid="field.value"
      v-slot="{ errors, valid }"
    )
      component(
        v-if="!field.hidden"
        v-bind="{...defaultAttrs, ...field.component_attrs}"
        v-on="field.component_events"
        :value="value[field.value]"
        :is="field.component"
        :name="field.value"
        :label="field.text"
        :error-messages="errors"
        :persistent-hint="!!fieldsState[field.value].hint"
        :hint="fieldsState[field.value].hint"
        @input="update(field.value, $event)"
        @change="update(field.value, $event)"
      )
        template(
          v-for="(template, idx) in field.templates"
          v-slot:[template]
        )
          slot(
            :name="`${field.value}.${template}`"
          )
    slot(name="preppend-actions")
    template(v-if="!autosave")
      slot(name="action")
        v-btn(
          v-bind="btnProps"
          rounded
          depressed
          color="success"
          :disabled="invalid"
          :loading="loading"
          @click="handleWrapper(save)"
        )
          span {{ btnProps.btnText || 'Сохранить' }}
          v-icon(
            v-if="btnProps.btnIcon"
            right
          ) {{ btnProps.btnIcon }}
        v-btn.ml-2(
          v-if="resetBtn"
          rounded
          depressed
          @click="reset"
        ) Сброс
</template>

<script>
import debounce from 'lodash/debounce';

export default {
  name: 'ApiForm',
  props: {
    // Fields values
    value: { type: Object, default: () => ({}) },

    // On save method
    method: { type: Function, required: true },

    // Build form fields
    /**
     * Field object structure:
     *
     * @param {String} value - The field name
     * @param {String} text - The field humanized name
     * @param {String} rules - Vee validation rules
     * @param {Object | String} component - The component string name or instance
     * @param {Array} component_attrs - Array of properties for the field component
     * @param {Array} component_events - Array of events for the field component
     * @param {Boolean} hidden - Hide/show the field
     */
    fields: { type: Array, required: true },

    // Inital values of fields
    initial: { type: Object, default: () => ({}) },

    // Call passed function on success saving
    successCallback: { type: Function, default: () => {} },

    // Call passed function on error saving
    errorCallback: { type: Function, default: () => {} },

    // The serialize function for payload data
    serializeData: { type: Function, default: (data) => data },

    // Props for the save btn
    btnProps: { type: Object, default: () => ({}) },

    handleWrapper: { type: Function, default: (callback) => callback() },

    // Show/hide the reset button
    resetBtn: { type: Boolean, default: false },

    // Triggering the save method on every @change input event
    autosave: { type: Boolean, default: false },

    defaultAttrs: { type: Object, default: () => {} },
  },
  data() {
    return {
      loading: false,

      fieldsState: {
        // [field-key]: { hint, loading }
      },
    };
  },
  watch: {
    fields: {
      handler(val) {
        val.forEach((field) => {
          if (!this.fieldsState[field.value]) {
            this.$set(this.fieldsState, field.value, {
              hint: null,
              loading: false,
              saved: false,
            });
          }
        });
      },
      immediate: true,
    },
  },
  methods: {
    executeMethod(payload) {
      return this.method({
        data: this.serializeData(payload),
        obs: this.$refs.obs,
      }).then((resp) => {
        this.successCallback(resp);
      }).catch((error) => {
        this.errorCallback(error);
      });
    },
    async save() {
      this.loading = true;
      const valid = await this.$refs.obs.validate();
      if (valid) {
        const payload = this.fields.reduce((acc, field) => {
          if (!field.local) return { ...acc, [field.value]: this.value[field.value] };
          return acc;
        }, {});
        await this.executeMethod(payload);
      }
      this.loading = false;
    },
    update(key, value) {
      this.$emit('input', { ...this.value, [key]: value });

      if (this.autosave) {
        this.overrideField(key, value);
      }
    },
    overrideField: debounce(async function (key, value) {
      this.fieldsState[key].loading = true;
      this.fieldsState[key].hint = 'Сохранение...';
      await this.executeMethod({ [key]: value });
      this.fieldsState[key].loading = false;
      this.fieldsState[key].hint = 'Сохранено';
      setTimeout(() => {
        this.fieldsState[key].hint = null;
      }, 1000);
    }, 1000),
    reset() {
      if (this.$refs.obs) {
        this.$refs.obs.reset();
        this.$emit('input', { ...this.initial });
      }
    },
  },
};
</script>
